import React, { useCallback, useEffect, useRef, useState } from 'react'
import { DebounceInput } from 'react-debounce-input'

import { formatMoney, regexPhoneNumber } from 'helpers/helpers'

import ChequePanel from 'pages/admin/sql/ChequePanel'
import { chequesQuery } from 'pages/admin/sql/queries'
import { formatPosDate } from 'pages/admin/sql/format'

const ORDER_TYPE_OPTIONS = [
	{ 'id': null, 'name': 'Все' },
	{ 'id': 1, 'name': 'Зал' },
	{ 'id': 2, 'name': 'С собой' },
	{ 'id': 3, 'name': 'Доставка' },
]

function parseJson(value) {
	if (!value) return null
	if (typeof value === 'object') return value

	try {
		return JSON.parse(value)
	} catch (error) {
		return null
	}
}

/**
 * Экран открытых чеков, как он выглядит на кассе: слева список, справа
 * выбранный чек. Колонки те же — номер, зал/стол, сотрудник, время, сумма,
 * — чтобы при разговоре с точкой обе стороны смотрели на одно и то же.
 */
function ChequesTab({ monitor, schema, schemaLoading }) {
	// Именно query, а не monitor: хук отдаёт новый объект на каждый рендер, и
	// эффект, завязанный на него, перезапускал бы выборку бесконечно.
	const query = monitor.query

	const [rows, setRows] = useState([])
	const [error, setError] = useState(null)
	const [tookMs, setTookMs] = useState(null)
	const [loadedAt, setLoadedAt] = useState(null)
	const [loading, setLoading] = useState(false)

	const [search, setSearch] = useState('')
	const [orderTypeId, setOrderTypeId] = useState(null)
	const [onlyOpen, setOnlyOpen] = useState(true)

	const [selectedId, setSelectedId] = useState(null)

	// Растёт только по кнопке «Обновить», не при смене фильтров: нажатие должно
	// освежить весь экран, включая содержимое открытого справа чека, а смена
	// фильтра касается только списка.
	const [refreshToken, setRefreshToken] = useState(0)

	// Запросы монитора и так идут по одному, но слать новую выборку, пока не
	// пришёл ответ на предыдущую, всё равно незачем: на медленной кассе
	// повторные нажатия «Обновить» накопили бы очередь.
	const loadingRef = useRef(false)

	const load = useCallback(async () => {
		if (!schema || loadingRef.current) return

		loadingRef.current = true
		setLoading(true)

		const result = await query(chequesQuery(schema, {
			'limit': 60,
			'onlyOpen': onlyOpen,
			'search': search,
			'orderTypeId': orderTypeId,
		}))

		loadingRef.current = false
		setLoading(false)
		setError(result.error)
		setTookMs(result.took_ms)

		if (!result.error) {
			setRows(result.rows)
			setLoadedAt(Date.now())
		}
	}, [query, schema, onlyOpen, search, orderTypeId])

	// Единственное автоматическое обновление — при смене фильтров: без него
	// поиск и переключатели просто не работали бы. Периодического опроса нет,
	// список обновляется кнопкой.
	useEffect(() => {
		load()
	}, [load])

	// Выбранный чек берём из свежего списка по id, а не держим копию: иначе
	// после обновления справа осталась бы старая сумма и старый статус.
	const selectedCheque = rows.find((item) => item.id === selectedId) || null

	const totalAmount = rows.reduce(
		(sum, item) => sum + Number(item.total_amount_service ?? item.total_amount ?? 0),
		0
	)

	function renderPlace(item) {
		const orderType = Number(item.order_type_resolved ?? item.order_type)
		const externalInfo = parseJson(item.external_info)
		const phone = item.client_phone || externalInfo?.phoneNumber

		let title = 'Зал'
		if (orderType === 2) title = 'С собой'
		if (orderType === 3) title = 'Доставка'
		if (orderType === 1) {
			title = (item.hall_name || item.table_name)
				? `${item.hall_name || ''} / ${item.table_name || ''}`
				: 'Зал'
		}

		return (
			<div>
				<div className="d-flex gap-1 align-items-center">
					<span className={Number(item.status) === 0 ? 'text-danger' : ''}>{title}</span>
					{/* Иконки те же, что в кассе: по ним видно источник заказа. */}
					{Number(item.order_type_delivery) === 2 &&
						<i className="uil-i uil-telegram text-primary" title="Telegram"></i>
					}
					{Number(item.order_type_delivery) === 3 &&
						<i className="uil-i uil-headphones-alt text-primary" title="Колл-центр"></i>
					}
					{Number(item.order_type_delivery) > 3 &&
						<i className="uil-i uil-cloud text-primary" title="Внешний сервис"></i>
					}
					{!!item.courier_id &&
						<i className="uil-i uil-truck text-primary" title="Курьер назначен"></i>
					}
				</div>
				{phone &&
					<div className="text-muted fz12">{regexPhoneNumber(phone)}</div>
				}
				{item.external_name &&
					<div className="text-muted fz12">
						{item.external_name}{item.external_id ? ` · ${item.external_id}` : ''}
					</div>
				}
			</div>
		)
	}

	return (
		<>
			<div className="card mb-3">
				<div className="card-body">
					<div className="row">
						<div className="form-group col-md-4">
							<label>Поиск</label>
							<DebounceInput
								type="text"
								className="form-control"
								placeholder="Номер чека, cloud_id, номер заказа агрегатора, телефон"
								value={search}
								minLength={1}
								debounceTimeout={600}
								onChange={(e) => setSearch(e.target.value)} />
						</div>
						<div className="form-group col-md-3">
							<label>Тип заказа</label>
							<div className="btn-group w-100">
								{ORDER_TYPE_OPTIONS.map((option) => (
									<button key={String(option.id)} type="button"
										className={`btn ${orderTypeId === option.id ? 'btn-primary' : 'btn-outline-primary'}`}
										onClick={() => setOrderTypeId(option.id)}>
										{option.name}
									</button>
								))}
							</div>
						</div>
						<div className="form-group col-md-5">
							<label>&nbsp;</label>
							<div className="d-flex align-items-center gap-3">
								<div className="form-check mb-0">
									<input className="form-check-input" type="checkbox" id="only-open"
										checked={onlyOpen}
										onChange={(e) => setOnlyOpen(e.target.checked)} />
									<label className="form-check-label" htmlFor="only-open">
										Только открытые
									</label>
								</div>
								<button type="button" className="btn btn-outline-primary"
									disabled={loading || !schema}
									onClick={() => { setRefreshToken((prev) => prev + 1); load() }}>
									Обновить
								</button>
							</div>
						</div>
					</div>

					<div className="d-flex gap-3 fz12 text-muted">
						<span>Строк: {rows.length}</span>
						{tookMs !== null && <span>Ответ кассы: {tookMs} мс</span>}
						{loadedAt && <span>Обновлено: {formatPosDate(loadedAt, 'HH:mm:ss')}</span>}
						{loading && <span>Запрос выполняется…</span>}
					</div>
				</div>
			</div>

			{error &&
				<div className="alert alert-danger">{error}</div>
			}

			<div className="row">
				<div className="col-md-7">
					<div className="card">
						<div className="card-body">
							<div className="table-responsive">
								<table className="table table-bordered mb-0">
									<thead>
										<tr>
											<th style={{ 'width': '10%' }}>№</th>
											<th style={{ 'width': '35%' }}>Зал / стол</th>
											<th style={{ 'width': '25%' }}>Сотрудник</th>
											<th style={{ 'width': '15%' }} className="text-end">Время</th>
											<th style={{ 'width': '15%' }} className="text-end">Сумма</th>
										</tr>
									</thead>
									<tbody>
										{rows.map((item) => (
											<tr key={item.id}
												style={{ 'cursor': 'pointer' }}
												className={selectedId === item.id ? 'table-active' : ''}
												onClick={() => setSelectedId(item.id)}>
												<td title={item.cloud_id ? `cloud_id ${item.cloud_id}` : 'В облаке чека нет'}>
													<span className={Number(item.cheque_printed) === 1 ? 'fw-bold text-danger' : ''}>
														{item.cheque_number ?? item.id}
													</span>
													{!item.cloud_id &&
														<i className="uil-i uil-cloud-slash text-warning ms-1"
															title="В облаке чека нет"></i>
													}
												</td>
												<td>{renderPlace(item)}</td>
												<td className="td-ellipsis">
													{item.waiter_name || item.cashier_name
														|| item.waiter_login || item.cashier_login
														|| item.operator_name || '—'}
												</td>
												<td className="text-end text-nowrap"
													title={formatPosDate(item.cheque_opened_at, 'dd.MM.yyyy HH:mm:ss')}>
													{formatPosDate(item.cheque_opened_at, 'HH:mm')}
												</td>
												<td className="text-end text-nowrap">
													{formatMoney(item.total_amount_service ?? item.total_amount)}
												</td>
											</tr>
										))}

										{rows.length === 0 && !loading &&
											<tr>
												<td colSpan="5" className="text-center text-muted">
													{schema && 'Чеков нет'}
													{!schema && schemaLoading && 'Читаю схему базы кассы…'}
													{!schema && !schemaLoading && 'Касса не ответила — список пуст'}
												</td>
											</tr>
										}
									</tbody>
									{rows.length > 0 &&
										<tfoot>
											<tr>
												<td colSpan="4" className="text-end"><b>Всего</b></td>
												<td className="text-end text-nowrap"><b>{formatMoney(totalAmount)}</b></td>
											</tr>
										</tfoot>
									}
								</table>
							</div>
						</div>
					</div>
				</div>

				<div className="col-md-5">
					<ChequePanel
						monitor={monitor}
						schema={schema}
						cheque={selectedCheque}
						refreshToken={refreshToken} />
				</div>
			</div>
		</>
	)
}

export default ChequesTab
